import { useEffect,useRef, useState } from 'react';
import { AiOutlineExclamationCircle } from "react-icons/ai";

import config from '@/config';
import EyeClosed from "@/shared-components/icons/components/forms/PasswordEyeClosed";
import EyeOpen from "@/shared-components/icons/components/forms/PasswordEyeOpen";

import "./styles/TextInput.css";

const Input = ({
  label = "",
  placeholder = "",
  name = "",
  type = "text",
  value,
  onChange,
  error = null,
  onFocusClearError,
  element, 
  className = "",
  rows = 1,
  maxLength,
  maxRows, 
}) => {

  const errorMessage = error && error.error_code && error.element === element ?
    config.errorCodes[error.error_code] 
    : null;

  const hasError = !!errorMessage;

  const getInputClass = (baseClass) =>
    `${baseClass} ${hasError ? "error" : ""}`.trim();

  const handleFocus = () => {
    if (typeof onFocusClearError === "function") {
      onFocusClearError();
    }
  };

  // only use for password
  const [showPassword, setShowPassword] = useState(false);

  // ───── Auto-grow setup for textarea ─────
  const textareaRef = useRef(null);
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const fullHeight = el.scrollHeight;
    const maxH = maxRows ? maxRows * 24 : null; // adjust 24px per line if needed
    el.style.height = maxH && fullHeight > maxH
      ? `${maxH}px`
      : `${fullHeight}px`;
  };
  useEffect(() => {
    if (type === "textarea") {
      autoResize(textareaRef.current);
    }
  }, [value, maxRows, type]);

  if (type === "password") {
    const toggleShowPassword = () => setShowPassword(!showPassword);

    return (
      <div className="new-input-container">
        <div className="form__group field">
          <input
            type={showPassword ? "text" : "password"}
            className={getInputClass("form__field")}
            name={name}
            placeholder={placeholder}
            required
            onChange={onChange}
            onFocus={handleFocus}
            value={value}
          />
          <label htmlFor={name} className="form__label">
            {label}
          </label>
          {showPassword ? (
            <EyeClosed className="form_password" onClick={toggleShowPassword} />
          ) : (
            <EyeOpen className="form_password" onClick={toggleShowPassword} />
          )}
          {hasError && (
            <div className="error-icon-container">
              <AiOutlineExclamationCircle className="error-icon"/>
              <div className="error-tooltip">{errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    );
  } else if (type === "confirmPassword") {
    const toggleShowPassword = () => setShowPassword(!showPassword);

    return (
      <div className="new-input-container">
        <div className="form__group field">
          <input
            type={showPassword ? "text" : "password"}
            className={getInputClass("form__field")}
            name={name}
            placeholder={placeholder}
            required
            onChange={onChange}
            onFocus={handleFocus}
            value={value}
            data-field-type={type}
          />
          <label htmlFor={name} className="form__label">
            {label}
          </label>
          {showPassword ? (
            <EyeClosed className="form_password" onClick={toggleShowPassword} />
          ) : (
            <EyeOpen className="form_password" onClick={toggleShowPassword} />
          )}
          {hasError && (
            <div className="error-icon-container-2">
              <AiOutlineExclamationCircle className="error-icon-2"/>
              <div className="error-tooltip-2">{errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    );
  
  } else if (type === "username") {
    return (
      <div className="new-input-container">
        <div className="form__group field">
          <input
            type="text"
            className={getInputClass("form__field username")}
            name={name}
            placeholder={placeholder}
            required
            onChange={onChange}
            onFocus={handleFocus}
            value={value}
            data-field-type={type}
          />
          <label htmlFor={name} className="form__label">
            {label}
          </label>
          <span className="input-username-prefix">@</span>
          {hasError && (
            <div className="error-icon-container-1">
              <AiOutlineExclamationCircle className="error-icon-1" />
              <div className="error-tooltip-1">{errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    );
  } else if (type === "textarea") {
    return (
      <div className={`new-input-container ${className || ""}`}>
        <div className="form__group field field--textarea">
          <textarea
            ref={textareaRef}
            name={name}
            placeholder={placeholder}
            required
            rows={rows}
            maxLength={maxLength}
            className={getInputClass(`form__field input-textarea ${className}`)}
            onKeyDown={e => {
              const hasModifier = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
              if (e.key === 'Enter' && !hasModifier && e.target.scrollHeight <= e.target.clientHeight) {
                e.preventDefault();
              }
            }}
            onChange={e => {
              onChange(e);
              autoResize(e.target);
            }}
            onFocus={handleFocus}
            value={value}
            style={{ overflowY: maxRows ? 'auto' : undefined }}
            data-field-type={type}
            data-gramm="false"
            data-gramm_editor="false"
            spellCheck="false"
          />
          <label htmlFor={name} className="form__label">
            {label}
          </label>
          {hasError && (
            <div className="error-icon-container-1">
              <AiOutlineExclamationCircle className="error-icon-1" />
              <div className="error-tooltip-1">{errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div className="new-input-container">
        <div className="form__group field">
          <input
            type={type}
            className={getInputClass("form__field")}
            name={name}
            placeholder={placeholder}
            required
            onChange={onChange}
            onFocus={handleFocus}
            value={value}
            data-field-type={type}
          />
          <label htmlFor={name} className="form__label">
            {label}
          </label>
          {hasError && (
            <div className="error-icon-container-1">
              <AiOutlineExclamationCircle className="error-icon-1" />
              <div className="error-tooltip-1">{errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
};

export default Input;
